// Cookie utility functions
const setCookie = (name, value, days = 60) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = name + '=' + encodeURIComponent(JSON.stringify(value)) + ';expires=' + expires.toUTCString() + ';path=/';
};

const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) {
            try {
                return JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
            } catch (e) {
                return null;
            }
        }
    }
    return null;
};

// Get URL parameters
const getUrlParams = () => {
    const params = {};
    const urlParams = new URLSearchParams(window.location.search);
    
    // UTM parameters
    params.utm_source = urlParams.get('utm_source');
    params.utm_medium = urlParams.get('utm_medium');
    params.utm_campaign = urlParams.get('utm_campaign');
    params.utm_content = urlParams.get('utm_content');
    params.utm_term = urlParams.get('utm_term');
    
    // Click IDs
    params.fbclid = urlParams.get('fbclid');
    
    return params;
};

// Check if any tracking parameters exist
const hasTrackingParams = (params) => {
    return Object.values(params).some(value => value !== null && value !== '');
};

// Create tracking data object
const createTrackingData = (params) => {
    const data = {
        landing_page: window.location.href.split('?')[0],
        referrer: document.referrer || 'direct',
        timestamp: new Date().toISOString(),
        // UTM parameters - always include, empty string if not present
        utm_source: params.utm_source || '',
        utm_medium: params.utm_medium || '',
        utm_campaign: params.utm_campaign || '',
        utm_content: params.utm_content || '',
        utm_term: params.utm_term || '',
        // Click IDs - only fbclid
        fbclid: params.fbclid || ''
    };

    return data;
};

// Main tracking function
const trackVisit = () => {
    const params = getUrlParams();
    
    // Only process if we have tracking parameters
    if (!hasTrackingParams(params)) {
        console.log('UTM Tracking: No tracking parameters found');
        return;
    }

    const trackingData = createTrackingData(params);
    
    // Handle First Touch - only set if doesn't exist
    let firstTouch = getCookie('firstTouch');
    if (!firstTouch) {
        firstTouch = trackingData;
        setCookie('firstTouch', firstTouch);
        console.log('UTM Tracking: First Touch saved', firstTouch);
    } else {
        console.log('UTM Tracking: First Touch already exists', firstTouch);
    }

    // Handle Last Touch - always update
    setCookie('lastTouch', trackingData);
    console.log('UTM Tracking: Last Touch updated', trackingData);
};

// Track created and modified fields for cleanup
const createdFields = new WeakMap(); // form -> [created fields]
const modifiedFields = new WeakMap(); // form -> [{field, originalValue}]

// Check OneTrust consent
const hasTrackingConsent = () => {
    if (typeof OnetrustActiveGroups === 'undefined') {
        return false;
    }
    return OnetrustActiveGroups.includes('C0002');
};

// Cleanup function to remove created fields and reset modified fields
const cleanupFormFields = (form) => {
    // Remove created fields
    const createdFieldsList = createdFields.get(form);
    if (createdFieldsList) {
        createdFieldsList.forEach(field => {
            if (field.parentNode) {
                field.parentNode.removeChild(field);
                console.log('UTM Tracking: Removed created field', field.name);
            }
        });
        createdFields.delete(form);
    }

    // Reset modified fields to empty
    const modifiedFieldsList = modifiedFields.get(form);
    if (modifiedFieldsList) {
        modifiedFieldsList.forEach(({field, originalValue}) => {
            if (field) {
                field.value = '';
                console.log('UTM Tracking: Reset field', field.name, 'to empty value');
            }
        });
        modifiedFields.delete(form);
    }
};

// Populate individual form fields
const populateFormFields = (form, firstTouch) => {
    // Check OneTrust consent before proceeding
    const fieldMappings = {
        'utm_source': ['utm_source', 'first_touch_utm_source'],
        'utm_medium': ['utm_medium', 'first_touch_utm_medium'],
        'utm_campaign': ['utm_campaign', 'first_touch_utm_campaign'],
        'utm_content': ['utm_content', 'first_touch_utm_content'],
        'utm_term': ['utm_term', 'first_touch_utm_term'],
        'fbclid': ['fbclid', 'first_touch_fbclid'],
        'referrer': ['referrer', 'first_touch_referrer'],
        'landing_page': ['landing_page', 'first_touch_landing_page'],
        'timestamp': ['timestamp','first_touch_timestamp']
    };
    if (!hasTrackingConsent()) {
        console.log('UTM Tracking: OneTrust consent not granted (C0002 required)');
        Object.keys(fieldMappings).forEach((key) => {
            if (firstTouch[key] !== undefined) {
                let fieldFound = false;
                
                fieldMappings[key].forEach((fieldName) => {
                    const field = form.querySelector(`input[name="${fieldName}"]`);
                    if (field) {
                        field.value = '';
                        fieldFound = true;
                    }
                });
            }
        });
        return;
    }

    // Initialize tracking arrays for this form
    if (!createdFields.has(form)) {
        createdFields.set(form, []);
    }
    if (!modifiedFields.has(form)) {
        modifiedFields.set(form, []);
    }

    Object.keys(fieldMappings).forEach((key) => {
        if (firstTouch[key] !== undefined) {
            let fieldFound = false;
            
            fieldMappings[key].forEach((fieldName) => {
                const field = form.querySelector(`input[name="${fieldName}"]`);
                if (field) {
                    // Backup original value before modifying
                    const originalValue = field.value;
                    modifiedFields.get(form).push({field, originalValue});
                    
                    field.value = firstTouch[key];
                    fieldFound = true;
                    console.log('UTM Tracking: Populated field', fieldName, 'with', firstTouch[key]);
                }
            });
            
            // If no field found, create hidden field
            if (!fieldFound) {
                const hiddenField = document.createElement('input');
                hiddenField.type = 'hidden';
                
                // Set field name based on key type
                if (key.startsWith('utm_')) {
                    hiddenField.name = key;
                    hiddenField.value = `${firstTouch[key]}`;
                } else if (key === 'fbclid') {
                    hiddenField.name = 'fbclid';
                    hiddenField.value = firstTouch[key];
                } else if (key === 'referrer') {
                    hiddenField.name = 'referrer';
                    hiddenField.value = firstTouch[key];
                } else if (key === 'landing_page') {
                    hiddenField.name = 'landing_page';
                    hiddenField.value = firstTouch[key];
                } else if (key === 'timestamp') {
                    hiddenField.name = 'timestamp';
                    hiddenField.value = firstTouch[key];
                }
                
                form.appendChild(hiddenField);
                
                // Track created field for cleanup
                createdFields.get(form).push(hiddenField);
                
                console.log('UTM Tracking: Created hidden field', hiddenField.name, 'with value', hiddenField.value);
            }
        }
    });

    // Add Last Touch as JSON to hidden field (with consent check)
    const lastTouch = getCookie('lastTouch');
    if (lastTouch && hasTrackingConsent()) {
        const lastTouchField = form.querySelector('input[name="last_touch"]') || 
                             form.querySelector('input[name="lastTouch"]') ||
                             form.querySelector('input[name="last_touch_data"]');
        
        if (lastTouchField) {
            // Field exists, add value
            const originalValue = lastTouchField.value;
            modifiedFields.get(form).push({field: lastTouchField, originalValue});
            
            lastTouchField.value = JSON.stringify(lastTouch);
            console.log('UTM Tracking: Last Touch data added to existing field', lastTouch);
        } else {
            // Field doesn't exist, create new hidden field
            const newLastTouchField = document.createElement('input');
            newLastTouchField.type = 'hidden';
            newLastTouchField.name = 'last_touch';
            newLastTouchField.value = JSON.stringify(lastTouch);
            form.appendChild(newLastTouchField);
            
            // Track created field for cleanup
            createdFields.get(form).push(newLastTouchField);
            
            console.log('UTM Tracking: Created new last_touch field with data', lastTouch);
        }
    }
};

// Populate HubSpot forms with First Touch data
const populateHubSpotForms = () => {
    const firstTouch = getCookie('firstTouch');
    if (!firstTouch) return;

    // Wait for HubSpot forms to load
    const checkForForms = setInterval(() => {
        const hubspotForms = document.querySelectorAll('form[data-form-id]');
        
        if (hubspotForms.length > 0) {
            clearInterval(checkForForms);
            
            hubspotForms.forEach((form) => {
                populateFormFields(form, firstTouch);
            });
        }
    }, 500);

    // Stop checking after 10 seconds
    setTimeout(() => {
        clearInterval(checkForForms);
    }, 10000);
};

// Get tracking data for external use
const getTrackingData = () => {
    return {
        firstTouch: getCookie('firstTouch'),
        lastTouch: getCookie('lastTouch')
    };
};

// Handle OneTrust consent changes
const handleConsentChange = () => {
    if (hasTrackingConsent()) {
        console.log('UTM Tracking: OneTrust consent granted, populating forms');
        populateHubSpotForms();
    } else {
        console.log('UTM Tracking: OneTrust consent revoked, cleaning up forms');
        // Cleanup all forms that have tracking data
        const allForms = document.querySelectorAll('.hbspt-form form');
        allForms.forEach(form => {
            if (createdFields.has(form) || modifiedFields.has(form)) {
                cleanupFormFields(form);
            }
        });
    }
};

// Main initialization function
export const initUTMTracking = () => {
    console.log('UTM Tracking initialized');
    
    // Track current visit
    trackVisit();
    
    // Populate HubSpot forms (if consent already granted)
    populateHubSpotForms();
    
    // Listen for OneTrust consent changes
    window.addEventListener('OneTrustGroupsUpdated', handleConsentChange);
    
    // Alternative OneTrust event listener (for different OneTrust versions)
    if (typeof OptanonWrapper === 'function') {
        const originalOptanonWrapper = window.OptanonWrapper;
        window.OptanonWrapper = function() {
            if (originalOptanonWrapper) {
                originalOptanonWrapper.apply(this, arguments);
            }
            handleConsentChange();
        };
    }
    
    // Listen for HubSpot form events
    window.addEventListener('message', (event) => {
        if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
            const firstTouch = getCookie('firstTouch');
            if (firstTouch && hasTrackingConsent()) {
                const targetForm = document.querySelector(`.hs-form-${event.data.id}`);
                if (targetForm) {
                    populateFormFields(targetForm, firstTouch);
                }
            }
        }
    });
    
    // Expose functions globally for external use
    window.UTMTracking = {
        getTrackingData,
        getFirstTouch: () => getCookie('firstTouch'),
        getLastTouch: () => getCookie('lastTouch'),
        populateHubSpotForms,
        hasTrackingConsent
    };
}; 